import { Strophe, $pres } from 'react-strophe'
import { Chat } from './chatSettingsConfig'
import { toStanza } from './stanzaHelper'

/**
 * Create a random unique id for each request.
 * NOTE: Could also use: Strophe.Connection.getUniqueId
 */
export const getUniqueId = () => {
  return (
    Date.now().toString(36) +
    Math.random()
      .toString(36)
      .substr(2, 5)
  ).toUpperCase()
}

const escapeSpecialChars = (str) => {
  if (str) {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;')
  }
  return str
}

/**
 * Create a query to find my contacts (roster).
 * https://xmpp.org/extensions/xep-0321.pdf
 */
export const payloadToGetRoster = () => {
  // return $iq({ type: 'get', id: getUniqueId() }).c('query', { xmlns: Chat.NS.ROSTER })
  return toStanza(`
    <iq type='get' id='${getUniqueId()}' xmlns="jabber:client">
      <query xmlns='${Chat.NS.ROSTER}'/>
    </iq>
  `)
}

export const payloadToConfirmPresenceSubscription = ({ toJid }) => {
  return toStanza(`
    <presence to="${toJid}" type="subscribed" xmlns="jabber:client"/>
  `)
}

export const payloadToAskPresenceSubscription = ({ toJid }) => {
  return toStanza(`
    <presence to="${toJid}" type="subscribe" xmlns="jabber:client"/>
  `)
}

/*
  Create a payload to remove this person from my roster.
*/
export const payloadToRemovePerson = ({ toJid }) => {
  return toStanza(`
    <iq id="${getUniqueId()}" type="set" xmlns="jabber:client">
      <query xmlns="${Chat.NS.ROSTER}">
        <item jid="${toJid}" subscription="remove"/>
      </query>
    </iq>
  `)
}

/*
  Create a payload to add a person.
*/
export const payloadToAddPerson = ({ toJid }) => {
  return toStanza(`
    <iq id="${getUniqueId()}" type="set" xmlns="jabber:client">
      <query xmlns="${Chat.NS.ROSTER}">
        <item jid="${toJid}"/>
      </query>
    </iq>
  `)
}

/**
 * Create payload to set my status.
 */
export const payloadToSetPresenceStatus = () => {
  const presence = $pres()

  // eslint-disable-next-line
  presence.c('priority').t(1).up()
  return presence.tree()

  // return toStanza(`
  //   <presence xmlns="jabber:client">
  //     <priority>1</priority>
  //   </presence>
  // `)
}

/*
 * Create a query to find archived messages between two people.
 */
export const payloadToGetChatHistory = ({
  toJid,
  paginationToken,
  count = Chat.Settings.DEFAULT_CHAT_HISTORY_COUNT,
}) => {
  const beforeTag = paginationToken ? `<before>${paginationToken}</before>` : `<before />`
  return toStanza(`
    <iq id="${getUniqueId()}" type="set" xmlns="jabber:client">
      <query queryid="${getUniqueId()}" xmlns="${Chat.NS.MAM_2}">
        <x type="submit" xmlns="${Chat.NS.XFORM}">
          <field var="FORM_TYPE" type="hidden">
            <value>${Chat.NS.MAM_2}</value>
          </field>
          <field var="with">
            <value>${toJid.toLowerCase()}</value>
          </field>
        </x>
        <set xmlns="${Chat.NS.RSM}">
          <max>${count}</max>
          ${beforeTag}
        </set>
      </query>
    </iq>
  `)

  // const iq = $iq({ id: getUniqueId(), type: 'set' })
  //   .c('query', { queryid: getUniqueId(), xmlns: 'urn:xmpp:mam:2' })
  //   .c('x', { type: 'submit', xmlns: 'jabber:x:data' })
  //   .c('field', { type: 'hidden', var: 'FORM_TYPE' })
  //   .c('value', {}, 'urn:xmpp:mam:2')
  //   .up()
  //   .c('field', { var: 'with' })
  //   .c('value', {}, `${to}`)
  //   .up()
  //   .up()
  //   .c('set', { xmlns: 'http://jabber.org/protocol/rsm' })
  //   .c('max', {}, '50')
  //   .c('before')
  // return iq
}

/*
 * Create a query to find archived messages in a chat room.
 */
export const payloadToGetChatRoomHistory = ({
  chatRoom,
  paginationToken,
  count = Chat.Settings.DEFAULT_CHAT_HISTORY_COUNT,
}) => {
  const beforeTag = paginationToken ? `<before>${paginationToken}</before>` : `<before />`
  return toStanza(`
    <iq type='set' id='${getUniqueId()}' to='${chatRoom}'>
      <query xmlns='${Chat.NS.MAM_2}'>
        <x xmlns='${Chat.NS.XFORM}' type='submit'>
          <field var='FORM_TYPE' type='hidden'>
            <value>${Chat.NS.MAM_2}</value>
          </field>
        </x>
        <set xmlns="${Chat.NS.RSM}">
          <max>${count}</max>
          ${beforeTag}
        </set>
      </query>
    </iq>
  `)
}

/**
 * Create the payload to send a message to a person.
 */
export const payloadToSendDirectMessage = ({ toJid, fromJid, fromResource, message, fromDisplayName }) => {
  const messageToSend = escapeSpecialChars(message)
  const fromName = escapeSpecialChars(fromDisplayName)

  // const m = $msg({ to, from, type: 'chat' }).c('body').t(message)
  return toStanza(`
    <message to='${toJid.toLowerCase()}' from='${fromJid.toLowerCase()}' type='chat' xmlns='jabber:client' id="${getUniqueId()}">
      <body>${messageToSend}</body>
      <active xmlns='${Chat.NS.CHATSTATES}'/>
      <rtc-message xmlns='${Chat.NS.RTC_MESSAGE}' from='${fromName}' from-resource='${fromResource}' />
    </message>
  `)
}

/*
  Create the payload to send a message to a chat room.
  https://xmpp.org/extensions/xep-0045.html#message
*/
export const payloadToSendMessageToChatRoom = ({ chatRoom, fromJid, fromResource, message, fromDisplayName }) => {
  const messageToSend = escapeSpecialChars(message)
  const fromName = escapeSpecialChars(fromDisplayName)

  return toStanza(`
    <message from='${fromJid.toLowerCase()}' id='${getUniqueId()}' to='${chatRoom}' type='groupchat' xmlns='jabber:client'>
      <body>${messageToSend}</body>
      <active xmlns='${Chat.NS.CHATSTATES}'/>
      <origin-id id='${getUniqueId()}' xmlns='${Chat.NS.SID}'/>
      <rtc-message xmlns='${Chat.NS.RTC_MESSAGE}' from='${fromName}' from-resource='${fromResource}' />
    </message>
  `)
}

/*
  Create a new group chat. By declaring your presence in the group,
  it will create the room if it does not exist already.

  This also will join a chatroom based on an invitation.

  NOTE: For now, only get the last 1 item of history since we just 
  want to be notified of new messages, not get all the messages until 
  they enter the chat room.
  https://xmpp.org/extensions/xep-0045.html#example-38
  https://xmpp.org/extensions/xep-0045.html#example-154
*/
export const payloadToCreateOrJoinChatRoom = ({ chatRoom, fromJid }) => {
  const nickname = Strophe.getNodeFromJid(fromJid)
  return toStanza(`
    <presence from='${fromJid}' xmlns="jabber:client" to='${chatRoom.toLowerCase()}/${nickname}'>
      <x xmlns='${Chat.NS.MUC}'>
        <history maxstanzas='1' />
      </x>
    </presence>
  `)
}

/*
  Update a person's presence in the chatroom.
  https://xmpp.org/extensions/xep-0045.html#exit
*/
export const payloadToLeaveChatRoom = ({ fromJid, chatRoom }) => {
  return toStanza(`
    <presence from='${fromJid}' to='${chatRoom}' type='${Chat.PersonStatus.UNAVAILABLE}' xmlns="jabber:client"/>
  `)
}

/*
  Fetch the list of members in a group.
  https://xmpp.org/extensions/xep-0045.html#modifymember
*/
export const payloadToGetMembersInChatRoom = ({ fromJid, chatRoom }) => {
  return toStanza(`
    <iq from='${fromJid}'
      id='${getUniqueId()}'
      to='${chatRoom}'
      xmlns="jabber:client"
      type='get'>
      <query xmlns='${Chat.NS.DISCO_ITEMS}' />
    </iq>
  `)
}

/*
  Searches the MUC by name
*/
export const payloadToSearchForChatRoom = ({ chatRoom }) => {
  return toStanza(`
    <iq type='get'
        to='${chatRoom.toLowerCase()}'
        id='${getUniqueId()}'>
      <query xmlns='${Chat.NS.DISCO_INFO}' />
    </iq>
  `)
}

export const payloadToCreatePrivateChatRoom = ({ chatRoom, fromJid }) => {
  const nickname = Strophe.getNodeFromJid(fromJid)
  return toStanza(`
    <presence to="${chatRoom}/${nickname}" xmlns="jabber:client">
      <status/>
      <x xmlns="${Chat.NS.MUC}"/>
    </presence>
  `)
}

export const payloadToUpdatePrivateChatRoomConfiguration = ({ chatRoom, friendlyName = '' }) => {
  return toStanza(`
    <iq id="${getUniqueId()}" to="${chatRoom}" type="set" xmlns="jabber:client">
      <query xmlns="${Chat.NS.MUC_OWNER}">
        <x type="submit" xmlns="${Chat.NS.XFORM}">
          <field type="hidden" var="FORM_TYPE">
            <value>${Chat.NS.MUC_ROOMCONFIG}</value>
          </field>
          <field type="boolean" var="muc#roomconfig_publicroom">
            <value>0</value>
          </field>
          <field type="text-single" var="muc#roomconfig_roomname">
            <value>${escapeSpecialChars(friendlyName)}</value>
          </field>
        </x>
      </query>
    </iq>
  `)
}

export const payloadToSendMessageInvitationToPrivateChatRoom = ({
  toJid,
  chatRoom,
  invitationMessage = 'Someone is inviting you to a group chat.',
}) => {
  return toStanza(`
    <message to="${toJid}" xmlns="jabber:client">
      <x jid="${chatRoom}" reason="${invitationMessage}" xmlns="${Chat.NS.XCONFERENCE}">
        <impromptu xmlns="${Chat.NS.IMPROMPTU}"/>
      </x>
    </message>
  `)
}

export const payloadToConfigureMessageCarbons = ({ fromJid }) => {
  return toStanza(`
    <iq id="${getUniqueId()}" type="set" from="${fromJid}" xmlns="jabber:client">
      <enable xmlns="${Chat.NS.CARBONS}" />
    </iq>
  `)
}

export const payloadToSetChatState = ({ toJid, fromJid, isGroup, chatState }) => {
  const type = isGroup ? 'groupchat' : 'chat'
  return toStanza(`
    <message xmlns='jabber:client' to='${toJid}' from='${fromJid}' type='${type}' id='${getUniqueId()}'>
      <${chatState} xmlns='${Chat.NS.CHATSTATES}'/>
    </message>
  `)
}

export const payloadToPing = () => {
  return toStanza(`
    <iq to='${Chat.Settings.DOMAIN}' type='get' id='ping'>
      <ping xmlns='${Chat.NS.PING}' />
    </iq>
  `)
}

export const payloadToProbePresence = ({ toJid, fromJid }) => {
  return toStanza(`
    <presence from="${fromJid}" to="${toJid}" id='${getUniqueId()}' type='probe' />
  `)
}
